import ClearIcon from '@mui/icons-material/Clear';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SearchIcon from '@mui/icons-material/Search';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewListIcon from '@mui/icons-material/ViewList';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import SettingsIcon from '@mui/icons-material/Settings';
import { Divider, IconButton, Stack, ToggleButton, Tooltip, Typography } from "@mui/material";
import { StagesFolders } from 'Modules/Core/Applicants/ApplicantsConstants';
import { ApplicantsFilters } from 'Modules/Core/Applicants/ApplicantsList/ApplicantsFilters';
import { IApplicantsToolBar, TInterviewsStage, TInterviewStage } from 'Modules/Core/Applicants/ApplicantsModel';
import { useEffect, useState } from 'react';
import { ILabelValueBase, LayoutTypes } from 'shared/SharedModels';
import { KeyCodes } from 'shared/constants';
import { areAllElementsInArrayXInArrayY, toTitleCase } from 'shared/utils';
import { AutoRejectDialog } from './AutoRejectDialog';
import { ResumeSearchDialog } from './ResumeSearchDialog';
import {
    StyledToolbarPaper,
    StyledSearchPaper,
    StyledInputBase,
    StyledToggleButtonGroup,
    StyledActionButton,
    StyledFilterChipsContainer,
    StyledStageButton,
    StyledBetaChip,
    StyledFilterChip,
    StyledIconButton,
} from './ApplicantsToolBar.styles';
import { ShToggleButtonGroup } from 'shared/SharedStyles/ShInputs';
import { AddApplicantDialog } from './AddApplicantDialog';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import { useParams } from 'react-router-dom';

export const ApplicantsToolBar = ({ applicantList, layoutType, setLayoutType, applyFilters, selectedFilters,
    sortList, sortParams, removeFilter, isSmScreen, searchInApplicants, searchKey }: IApplicantsToolBar) => {
    const [sortByAnchorEl, setSortByAnchorEl] = useState<HTMLElement | null>(null);
    const isSortByOpen = Boolean(sortByAnchorEl);
    const [_searchKey, _setSearchKey] = useState<string>("");
    const [selectedStages, setSelectedStages] = useState<TInterviewsStage[]>([]);
    const [isAutoRejectOpen, setIsAutoRejectOpen] = useState(false);
    const [isResumeSearchOpen, setIsResumeSearchOpen] = useState(false);
    const [isAddApplicantOpen, setIsAddApplicantOpen] = useState(false);
    const { jobId } = useParams();
    // Update search key based on List component.
    useEffect(() => {
        _setSearchKey(() => { return searchKey });
    }, [searchKey]);

    const unselectAll = (currStages: (TInterviewsStage)[]) => {
        const newStages = [...currStages];
        newStages.splice(currStages.findIndex(stage => stage === 'all'), 1);
        setSelectedStages(newStages);
    }

    // Always update the selected stages array after filters are applied.
    useEffect(() => {
        const newSelectedStages: TInterviewsStage[] = [];
        const currSelectedStages = selectedFilters.stages.map(stage => stage.value);
        if (areAllElementsInArrayXInArrayY(['interview', 'second_interview', 'phone_screening'], currSelectedStages)) {
            newSelectedStages.push('interviews');
        }
        if (currSelectedStages.includes('new')) {
            newSelectedStages.push('new');
        }
        if (currSelectedStages.includes('rejected')) {
            newSelectedStages.push('rejected');
        }
        if (currSelectedStages.includes('reviewed')) {
            newSelectedStages.push('reviewed');
        }
        if (currSelectedStages.includes('offer')) {
            newSelectedStages.push('offer');
        }
        if (currSelectedStages.includes('hired')) {
            newSelectedStages.push('hired');
        }
        setSelectedStages([...newSelectedStages]);
        if (newSelectedStages.length === 0) {
            setSelectedStages(['all']);
        }
    }, [selectedFilters.stages]);

    const handleStageFilter = (stageValue: TInterviewsStage) => {
        if (stageValue === 'all') {
            // If 'All' is selected, remove the stage filter to show all applicants
            applyFilters({
                stages: [],
                fit: [],
                keywords: [],
                rating: []
            });
            if (selectedStages.includes('all')) {
                unselectAll([]);
            } else {
                setSelectedStages(['all']);
            }
        } else if (stageValue === 'interviews') {
            if (selectedStages.includes('interviews')) {
                const newStages = [...selectedStages];
                newStages.splice(selectedStages.findIndex(stage => stage === 'interviews'), 1);
                setSelectedStages(newStages);
                const appliedStageFilters = [...selectedFilters.stages];
                appliedStageFilters.splice(appliedStageFilters.findIndex(stage => stage.value === 'interview'), 1);
                appliedStageFilters.splice(appliedStageFilters.findIndex(stage => stage.value === 'second_interview'), 1);
                appliedStageFilters.splice(appliedStageFilters.findIndex(stage => stage.value === 'phone_screening'), 1);
                // If 'Interviews' is selected, apply filter for all interview-related stages
                applyFilters({
                    stages: appliedStageFilters,
                    fit: [],
                    keywords: [],
                    rating: []
                });
                if (selectedStages.includes('all')) {
                    unselectAll([...appliedStageFilters.map(stage => stage.value)]);
                }
            } else {
                // If 'Interviews' is selected, apply filter for all interview-related stages
                applyFilters({
                    stages: [
                        ...selectedFilters.stages,
                        { label: 'Interview', value: 'interview' },
                        { label: 'Final Interview', value: 'second_interview' },
                        { label: 'Phone Screening', value: 'phone_screening' }
                    ],
                    fit: [],
                    keywords: [],
                    rating: []
                });
                setSelectedStages([...selectedStages, stageValue]);
                if (selectedStages.includes('all')) {
                    unselectAll([...selectedStages, stageValue]);
                }
            }
        } else {
            const newFilters: ILabelValueBase<string, TInterviewStage>[] = [...selectedFilters.stages];
            if (newFilters.map(stage => stage.value).includes(stageValue)) {
                newFilters.splice(newFilters.findIndex(stage => stage.value === stageValue), 1);
            } else {
                newFilters.push({ label: toTitleCase(stageValue), value: stageValue });
            }
            applyFilters({
                stages: [...newFilters],
                fit: [],
                keywords: [],
                rating: []
            });
            setSelectedStages([...newFilters.map(stages => stages.value)]);
            if (selectedStages.includes('all')) {
                unselectAll([...selectedStages, stageValue]);
            }
        }
    };

    const hasFilters = selectedFilters.keywords?.length > 0 ||
        selectedFilters.fit?.length > 0 ||
        selectedFilters.rating?.length > 0 ||
        selectedFilters.stages?.length > 0;

    return (
        <StyledToolbarPaper variant="outlined">
            <Stack spacing={2}>
                {/* Top Row */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <StyledSearchPaper variant="outlined">
                        <StyledInputBase
                            id='search-applicants'
                            placeholder='Search applicants...'
                            value={_searchKey}
                            onChange={e => _setSearchKey(e.target.value)}
                            onKeyUp={e => e.code === KeyCodes.Enter && searchInApplicants(_searchKey)}
                        />
                        {searchKey?.length > 0 && (
                            <Tooltip title="Clear search">
                                <StyledIconButton
                                    type="button"
                                    aria-label="clear"
                                    color='error'
                                    onClick={() => searchInApplicants('')}
                                >
                                    <ClearIcon />
                                </StyledIconButton>
                            </Tooltip>
                        )}
                         <Tooltip title="Search">
                            <IconButton
                                disabled={_searchKey?.trim().length === 0}
                                type="button"
                                aria-label="search"
                                onClick={() => searchInApplicants(_searchKey)}
                            >
                                <SearchIcon />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem />
                        <ApplicantsFilters applyFilters={applyFilters} selectedFilters={selectedFilters} />
                    </StyledSearchPaper>

                    <Stack direction="row" spacing={1} alignItems="center">
                     
                        <StyledActionButton variant="outlined" onClick={() => setIsResumeSearchOpen(true)} startIcon={<SearchIcon />}>
                            Search Resumes
                        </StyledActionButton>

                        <StyledActionButton variant="outlined" onClick={() => setIsAddApplicantOpen(true)} startIcon={<AddBoxOutlinedIcon />}>
                            Create Applicant
                        </StyledActionButton>

                        <StyledActionButton variant="outlined" onClick={() => setIsAutoRejectOpen(true)} startIcon={<SettingsIcon />}>
                            Batch Auto-reject
                        </StyledActionButton>
                        


                        {!isSmScreen && (
                            <ShToggleButtonGroup exclusive value={layoutType} onChange={(e, newVal) => newVal !== null && setLayoutType(newVal)}
                                aria-label="toggle layout">
                                <ToggleButton value={LayoutTypes.List} aria-label="List">
                                    <ViewListIcon />
                                </ToggleButton>
                                <ToggleButton value={LayoutTypes.Table} aria-label="Table">
                                    <TableChartIcon />
                                </ToggleButton>
                                <ToggleButton value={LayoutTypes.Pipeline} aria-label="Pipeline View">
                                    <Stack direction='row' alignItems='center' spacing={1}>
                                        <LinearScaleIcon />
                                        <StyledBetaChip
                                            label="Beta"
                                            size="small"
                                        />
                                    </Stack>
                                </ToggleButton>
                            </ShToggleButtonGroup>
                        )}
                    </Stack>
                </Stack>

                {/* Bottom Row */}
                <Stack direction="row" alignItems="center">
                    <StyledToggleButtonGroup color="primary" size="small" exclusive value={selectedStages}>
                        {StagesFolders.map((stage) => (
                            <StyledStageButton value={stage.value} key={stage.value}
                                onClick={() => handleStageFilter(stage.value)}>
                                <FolderOpenIcon />
                                <Typography variant='body2'>
                                    {stage.label}
                                </Typography>
                            </StyledStageButton>
                        ))}
                    </StyledToggleButtonGroup>
                </Stack>

                {hasFilters && (
                    <StyledFilterChipsContainer>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {selectedFilters.stages?.map((f) => (
                                <StyledFilterChip
                                    key={f.value}
                                    variant='outlined'
                                    size='small'
                                    label={f.label}
                                    onDelete={() => removeFilter('stages', f.value)}
                                />
                            ))}
                            {selectedFilters.keywords?.map((f) => (
                                <StyledFilterChip
                                    key={f.label}
                                    variant='outlined'
                                    size='small'
                                    label={f.label}
                                    onDelete={() => removeFilter('keywords', f.value)}
                                />
                            ))}
                            {selectedFilters.fit?.map((f) => (
                                <StyledFilterChip
                                    key={f.label}
                                    variant='outlined'
                                    size='small'
                                    label={f.label}
                                    onDelete={() => removeFilter('fit', f.value)}
                                />
                            ))}
                            {selectedFilters.rating?.map((f) => (
                                <StyledFilterChip
                                    key={f.label}
                                    variant='outlined'
                                    size='small'
                                    label={'Rating: ' + f.value}
                                    onDelete={() => removeFilter('rating', f.value)}
                                />
                            ))}
                        </Stack>
                    </StyledFilterChipsContainer>
                )}
            </Stack>

            <AutoRejectDialog 
                open={isAutoRejectOpen} 
                onClose={() => setIsAutoRejectOpen(false)} 
            />

            <ResumeSearchDialog
                open={isResumeSearchOpen}
                onClose={() => setIsResumeSearchOpen(false)}
            />

            <AddApplicantDialog
                open={isAddApplicantOpen}
                onClose={() => setIsAddApplicantOpen(false)} employerJobId={parseInt(jobId ?? '0')}            />
        </StyledToolbarPaper>
    );
};